import React, { useMemo } from 'react'
import { Alert, SortableTable, Typography } from 'foundations-library/components'
import OutlinedContent from './OutlinedContent'
import { LiveReceptionistListCallResponse } from 'interfaces'
import { formatPhoneNumber, formatTime } from 'formatters'

const getDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60

    return minutes > 0
        ? `${minutes}min ${remainingSeconds}sec`
        : `${remainingSeconds}sec`
}

type PropsType = {
    items?: LiveReceptionistListCallResponse['items']
    search?: string
    loading?: boolean
    error?: string | null
}

const columns = [
    { label: 'Caller', accessor: 'callani', sortable: false },
    { label: 'Date & Time', accessor: 'time', sortable: true },
    { label: 'Direction', accessor: 'calldirection', sortable: true },
    { label: 'Duration', accessor: 'callduration', sortable: true },
    { label: 'Billed minutes', accessor: 'billedMinutes', sortable: true }
]

const sortCalls = (field: string, order: string, rows: Record<string, unknown>[]) => {
    const sortFields: Record<string, string> = { time: 'start_time', callduration: 'call_duration' }
    const sortField = sortFields[field] || field
    return [...rows].sort((a, b) => {
        const first = a[sortField]
        const second = b[sortField]
        const comparison = typeof first === 'number' && typeof second === 'number'
            ? first - second
            : String(first ?? '').localeCompare(String(second ?? ''), 'en', { numeric: true })
        return comparison * (order === 'asc' ? 1 : -1)
    })
}

/**
 *
 */
const CallList = ({ items, loading, error, search = '' }: PropsType): JSX.Element => {
    const data = useMemo(() => {
        if (!items) return []

        const rows = items.map((call) => ({
            callani: formatPhoneNumber(call.callani),
            time: formatTime(call.start_time, { day: '2-digit', month: '2-digit' }),
            calldirection: call.calldirection,
            callduration: getDuration(call.call_duration),
            start_time: call.start_time,
            call_duration: call.call_duration,
            billedMinutes: Math.ceil(call.call_duration / 60)

        }))
        const query = search.trim().toLowerCase()
        if (!query) return rows
        return rows.filter((row) => columns.some(({ accessor }) =>
            String(row[accessor] ?? '').toLowerCase().includes(query)
        ))
    }, [items, search])
    if (error) {
        return <Alert marginY={30}>{error}</Alert>
    }

    if (!loading && !data.length) {
        return <Typography variant='subtitle2' color='neutral'>No data available for your search</Typography>
    }

    return <OutlinedContent>
        <SortableTable columns={columns} data={data} loading={loading} sortingCb={sortCalls}></SortableTable>
    </OutlinedContent>
}

export default CallList
